import { LoadingManager, SRGBColorSpace, TextureLoader } from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import { resolveAsset } from '../utils/url.js';

/**
 * Small asset pipeline around Three.js loaders.
 *
 * Assets live in /public/assets and are referenced as '/assets/...'. The
 * resolver handles Vite's base path for production builds.
 */
export class Assets {
  constructor() {
    this.manager = new LoadingManager();
    this.manager.onProgress = (url, loaded, total) => {
      this.onProgress?.(total > 0 ? loaded / total : 0, url);
    };
    this.manager.onError = (url) => {
      console.error(`[Assets] Failed to load ${url}`);
    };

    this.gltfLoader = new GLTFLoader(this.manager);
    this.textureLoader = new TextureLoader(this.manager);
    this.gltfCache = new Map();
    this.onProgress = null;
  }

  resolve(path) {
    return resolveAsset(path);
  }

  /**
   * Load and cache a glTF/GLB. Use `instantiate()` for repeated copies.
   */
  async loadGLTF(path) {
    const url = this.resolve(path);

    let pending = this.gltfCache.get(url);
    if (!pending) {
      pending = this.gltfLoader.loadAsync(url);
      this.gltfCache.set(url, pending);
    }

    return pending;
  }

  /**
   * Clone a loaded glTF (including skinned meshes) for use in the scene.
   * Animations are shared; create an AnimationMixer per instance.
   */
  async loadModel(path) {
    const gltf = await this.loadGLTF(path);
    return {
      scene: cloneSkeleton(gltf.scene),
      animations: gltf.animations,
    };
  }

  async loadTexture(path, { srgb = true } = {}) {
    const texture = await this.textureLoader.loadAsync(this.resolve(path));
    if (srgb) texture.colorSpace = SRGBColorSpace;
    return texture;
  }

  dispose() {
    this.gltfCache.clear();
    this.onProgress = null;
  }
}
